import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import DiagnosisResult from "../components/DiagnosisResult";
import { fileUrl, downloadPdf } from "../lib/media";
import { Car, Bike, Trash2, Calendar, CheckCircle2, Circle, ChevronDown, Loader2, Inbox, FileDown, Camera, FileAudio } from "lucide-react";
import { toast } from "sonner";

export default function History() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get("/history");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const toggleResolved = async (item) => {
    await api.patch(`/history/${item.id}`, { resolved: !item.resolved });
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, resolved: !x.resolved } : x)));
    toast.success(!item.resolved ? "Segnato come risolto" : "Segnato come non risolto");
  };

  const remove = async (id) => {
    await api.delete(`/history/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id));
    toast.success("Diagnosi eliminata");
  };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

  if (items === null)
    return (
      <div className="pt-24 min-h-screen flex justify-center">
        <Loader2 className="w-8 h-8 text-[#F97316] animate-spin mt-16" />
      </div>
    );

  return (
    <div className="pt-24 pb-20 max-w-4xl mx-auto px-5">
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-2">Storico Diagnosi</h1>
      <p className="text-zinc-400 mb-8">Rivedi le tue diagnosi passate e segna i problemi risolti.</p>

      {items.length === 0 ? (
        <div className="rounded-2xl bg-[#111823] border border-white/8 p-12 text-center" data-testid="history-empty">
          <Inbox className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Nessuna diagnosi salvata.</p>
          <Link to="/diagnosi" className="inline-block px-5 py-2.5 rounded-lg bg-[#F97316] text-black font-semibold">Avvia una diagnosi</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => {
            const isOpen = open === item.id;
            const VIcon = item.vehicle_type === "moto" ? Bike : Car;
            return (
              <div key={item.id} data-testid="history-card-item" className="rounded-2xl bg-[#111823] border border-white/8 overflow-hidden">
                <div className="p-5 flex items-start gap-4">
                  <span className="w-11 h-11 rounded-xl bg-[#06B6D4]/10 flex items-center justify-center shrink-0">
                    <VIcon className="w-5 h-5 text-[#06B6D4]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-zinc-100 capitalize">{item.vehicle_type}</span>
                      {(item.brand || item.model) && (
                        <span className="text-sm text-zinc-400">{item.brand} {item.model}</span>
                      )}
                      {item.resolved && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Risolto</span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 mt-1 line-clamp-1">{item.result?.summary}</p>
                    <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-1.5 font-mono">
                      <Calendar className="w-3.5 h-3.5" /> {fmtDate(item.created_at)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button data-testid={`history-delete-${item.id}`} onClick={() => remove(item.id)} className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="px-5 pb-4 flex items-center gap-3 flex-wrap">
                  <button
                    data-testid={`history-toggle-resolved-${item.id}`}
                    onClick={() => toggleResolved(item)}
                    className="flex items-center gap-1.5 text-sm text-zinc-300 hover:text-emerald-400 transition-colors"
                  >
                    {item.resolved ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Circle className="w-4 h-4" />}
                    {item.resolved ? "Risolto" : "Segna come risolto"}
                  </button>
                  <button
                    data-testid={`history-pdf-${item.id}`}
                    onClick={() => downloadPdf(item.id)}
                    className="flex items-center gap-1.5 text-sm text-zinc-300 hover:text-[#F97316] transition-colors"
                  >
                    <FileDown className="w-4 h-4" /> PDF
                  </button>
                  <button
                    onClick={() => setOpen(isOpen ? null : item.id)}
                    className="ml-auto flex items-center gap-1 text-sm text-[#F97316] hover:underline"
                    data-testid={`history-expand-${item.id}`}
                  >
                    {isOpen ? "Nascondi" : "Dettagli"} <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {isOpen && (
                  <div className="px-5 pb-6 pt-2 border-t border-white/5 rise">
                    {item.symptoms?.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {item.symptoms.map((s, i) => (
                          <span key={i} className="text-xs px-2 py-1 rounded bg-white/5 text-zinc-300">{s}</span>
                        ))}
                      </div>
                    )}
                    {item.description && <p className="text-sm text-zinc-400 italic mb-4">"{item.description}"</p>}
                    {(item.photo_path || item.audio_path) && (
                      <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        {item.photo_path && (
                          <div>
                            <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1.5"><Camera className="w-3.5 h-3.5" /> Foto allegata</div>
                            <img src={fileUrl(item.photo_path)} alt="foto guasto" className="w-full h-40 object-cover rounded-lg" data-testid={`history-photo-${item.id}`} />
                          </div>
                        )}
                        {item.audio_path && (
                          <div>
                            <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1.5"><FileAudio className="w-3.5 h-3.5" /> Audio del rumore</div>
                            <audio controls src={fileUrl(item.audio_path)} className="w-full" data-testid={`history-audio-${item.id}`} />
                            {item.audio_transcript && <p className="text-xs text-zinc-400 italic mt-1.5">"{item.audio_transcript}"</p>}
                          </div>
                        )}
                      </div>
                    )}
                    <DiagnosisResult result={item.result} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
